/**
 * Portal Invoice Detail
 * Detailed view of a single invoice in the partner portal
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { formatCurrency, formatDate, isOverdue } from '../utils/formatters';
import {
  validatePortalAccess,
  getPortalInvoiceDetail,
  getPortalPaymentMethods,
  processManualPayment,
} from '../services/portalService';
import { ContractEquivalent } from '../components/ContractEquivalent';
import './PortalInvoiceDetail.css';

const PortalInvoiceDetail = () => {
  const { accessToken, invoiceId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [client, setClient] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [selectedPaymentOption, setSelectedPaymentOption] = useState(0); // Index of selected payment option

  useEffect(() => {
    const loadInvoice = async () => {
      try {
        setLoading(true);
        setError(null);

        // Validate access
        const accessResult = await validatePortalAccess(accessToken);
        if (!accessResult.success) {
          throw new Error('Invalid access');
        }
        setClient(accessResult.client);

        // Load invoice detail
        const invoiceResult = await getPortalInvoiceDetail(accessToken, invoiceId);
        if (!invoiceResult.success) {
          throw new Error('Invoice not found');
        }
        setInvoice(invoiceResult.invoice);

        // Load payment methods
        if (accessResult.permissions.canUpdatePayment) {
          const methodsResult = await getPortalPaymentMethods(accessToken);
          setPaymentMethods(methodsResult.paymentMethods || []);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error loading invoice:', err);
        setError(err.message || 'Failed to load invoice');
        setLoading(false);
      }
    };

    loadInvoice();
  }, [accessToken, invoiceId]);

  const handlePayNow = () => {
    if (!paymentMethods.length) {
      navigate(`/portal/${accessToken}/payment-setup`);
      return;
    }

    // Pre-select the default payment method
    const defaultMethod = paymentMethods.find(pm => pm.isDefault) || paymentMethods[0];
    setSelectedPaymentMethod(defaultMethod.id);
    setShowPaymentModal(true);
  };

  const handleConfirmPayment = async () => {
    if (!selectedPaymentMethod) {
      alert('Please select a payment method');
      return;
    }

    const selectedCard = paymentMethods.find(pm => pm.id === selectedPaymentMethod);

    setProcessingPayment(true);
    setShowPaymentModal(false);
    try {
      const result = await processManualPayment(accessToken, invoiceId, selectedPaymentMethod);
      if (result.success) {
        // Reload invoice
        const invoiceResult = await getPortalInvoiceDetail(accessToken, invoiceId);
        setInvoice(invoiceResult.invoice);
        alert('Payment successful!');
      } else {
        alert('Payment failed. Please try again or contact support.');
      }
    } catch (err) {
      alert('Payment error: ' + err.message);
    } finally {
      setProcessingPayment(false);
    }
  };

  const getBrandIcon = (brand) => {
    const icons = {
      visa: 'VISA',
      mastercard: 'MC',
      amex: 'AMEX',
      discover: 'DISC',
    };
    return icons[brand?.toLowerCase()] || 'CARD';
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="invoice-detail-container">
        <div className="invoice-loading">
          <div className="spinner"></div>
          <p>Loading invoice...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="invoice-detail-container">
        <div className="invoice-error">
          <h2>Error</h2>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={() => navigate(`/portal/${accessToken}`)}>
            Back to Portal
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="invoice-detail-container">
      <header className="invoice-detail-header">
        <button className="back-button" onClick={() => navigate(`/portal/${accessToken}`)}>
          &larr; Back to Portal
        </button>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={handlePrint}>
            Print / Save PDF
          </button>
          {invoice.status !== 'paid' && (
            <button
              className="btn btn-primary"
              onClick={handlePayNow}
              disabled={processingPayment}
            >
              {processingPayment ? 'Processing...' : 'Pay Now'}
            </button>
          )}
        </div>
      </header>

      <div className="invoice-document">
        <div className="invoice-header-section">
          <div className="invoice-branding">
            <h1>INVOICE</h1>
            <p className="invoice-number">{invoice.invoiceNumber}</p>
          </div>
          <div className={`invoice-status-badge ${invoice.status}`}>
            {invoice.status.toUpperCase()}
          </div>
        </div>

        <div className="invoice-parties">
          <div className="from-section">
            <h4>From</h4>
            <p className="company-name">FusionICT Billing</p>
            <p>Cloud Infrastructure Services</p>
          </div>
          <div className="to-section">
            <h4>Bill To</h4>
            <p className="company-name">{client?.name}</p>
            <p>{client?.email}</p>
          </div>
        </div>

        <div className="invoice-meta">
          <div className="meta-item">
            <span className="label">Project</span>
            <span className="value">{invoice.projectName}</span>
          </div>
          <div className="meta-item">
            <span className="label">Billing Period</span>
            <span className="value">{invoice.billingPeriod}</span>
          </div>
          <div className="meta-item">
            <span className="label">Issue Date</span>
            <span className="value">{formatDate(invoice.issueDate)}</span>
          </div>
          <div className="meta-item">
            <span className="label">Due Date</span>
            <span className={`value ${isOverdue(invoice.dueDate) && invoice.status !== 'paid' ? 'overdue' : ''}`}>
              {formatDate(invoice.dueDate)}
            </span>
          </div>
        </div>

        <div className="invoice-items">
          <table className="items-table">
            <thead>
              <tr>
                <th>Description</th>
                <th className="text-right">Qty</th>
                <th className="text-right">Unit Price</th>
                <th className="text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {invoice.lineItems?.map((item, index) => (
                <tr key={index}>
                  <td>{item.description}</td>
                  <td className="text-right">{item.quantity}</td>
                  <td className="text-right">{formatCurrency(item.unitPrice, invoice.currency)}</td>
                  <td className="text-right">{formatCurrency(item.amount, invoice.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="invoice-totals">
          <div className="totals-row">
            <span>Subtotal</span>
            <span>{formatCurrency(invoice.subtotal, invoice.currency)}</span>
          </div>
          {invoice.discountAmount > 0 && (
            <div className="totals-row discount">
              <span>Discount ({invoice.discountPercent || 0}%)</span>
              <span className="discount-amount">-{formatCurrency(invoice.discountAmount, invoice.currency)}</span>
            </div>
          )}
          {invoice.tax > 0 && (
            <div className="totals-row">
              <span>Tax</span>
              <span>{formatCurrency(invoice.tax, invoice.currency)}</span>
            </div>
          )}
          <div className="totals-row total">
            <span>Total Due</span>
            <span>
              {formatCurrency(invoice.totalAmount, invoice.currency)}
              <ContractEquivalent invoice={invoice} />
            </span>
          </div>
        </div>

        {invoice.status === 'paid' && (
          <div className="payment-info">
            <div className="paid-stamp">PAID</div>
            <p>Payment received on {formatDate(invoice.paidAt)}</p>
          </div>
        )}

        {/* Staggered Payment Options */}
        {invoice.status !== 'paid' && invoice.paymentOptions && invoice.paymentOptions.length > 1 && (
          <div className="payment-options-section">
            <h3>💳 Flexible Payment Options</h3>
            <p className="payment-options-subtitle">Choose the payment schedule that works best for you:</p>
            <div className="payment-options-grid">
              {invoice.paymentOptions.map((option, idx) => (
                <div
                  key={idx}
                  className={`payment-option-card ${selectedPaymentOption === idx ? 'selected' : ''} ${idx === 0 ? 'recommended' : ''}`}
                  onClick={() => setSelectedPaymentOption(idx)}
                >
                  <div className="option-header">
                    <span className="option-name">{option.name}</span>
                    {idx === 0 && <span className="recommended-badge">RECOMMENDED</span>}
                  </div>
                  <div className="option-amount">
                    <span className="amount">{formatCurrency(option.amount, invoice.currency)}</span>
                    {option.installments > 1 && (
                      <span className="multiplier">× {option.installments}</span>
                    )}
                  </div>
                  <p className="option-description">{option.description}</p>
                  {option.schedule && (
                    <div className="option-schedule">
                      <span className="schedule-label">Schedule:</span>
                      <span className="schedule-dates">{option.schedule.join(' → ')}</span>
                    </div>
                  )}
                  <div className="option-select-indicator">
                    {selectedPaymentOption === idx ? '✓ Selected' : 'Click to select'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <footer className="invoice-detail-footer">
        <p>Thank you for your business!</p>
      </footer>

      {/* Payment Method Selection Modal */}
      {showPaymentModal && (
        <div className="payment-modal-overlay" onClick={() => setShowPaymentModal(false)}>
          <div className="payment-modal" onClick={(e) => e.stopPropagation()}>
            <div className="payment-modal-header">
              <h3>Select Payment Method</h3>
              <button className="modal-close" onClick={() => setShowPaymentModal(false)}>&times;</button>
            </div>

            <div className="payment-modal-body">
              {invoice.paymentOptions && invoice.paymentOptions.length > 1 ? (
                <div className="selected-payment-option-info">
                  <p className="payment-option-label">{invoice.paymentOptions[selectedPaymentOption]?.name}</p>
                  <p className="payment-amount">
                    Amount to pay: <strong>{formatCurrency(invoice.paymentOptions[selectedPaymentOption]?.amount, invoice.currency)}</strong>
                    {invoice.paymentOptions[selectedPaymentOption]?.installments > 1 && (
                      <span className="installment-note">
                        (Payment 1 of {invoice.paymentOptions[selectedPaymentOption]?.installments})
                      </span>
                    )}
                  </p>
                </div>
              ) : (
                <p className="payment-amount">
                  Amount to pay: <strong>{formatCurrency(invoice.totalAmount, invoice.currency)}</strong>
                  <ContractEquivalent invoice={invoice} className="modal" />
                </p>
              )}

              <div className="payment-methods-list">
                {paymentMethods.map((pm) => (
                  <label
                    key={pm.id}
                    className={`payment-method-option ${selectedPaymentMethod === pm.id ? 'selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      value={pm.id}
                      checked={selectedPaymentMethod === pm.id}
                      onChange={() => setSelectedPaymentMethod(pm.id)}
                    />
                    <div className="payment-method-details">
                      <span className="card-brand">{getBrandIcon(pm.brand)}</span>
                      <span className="card-number">•••• {pm.last4}</span>
                      <span className="card-expiry">{pm.expMonth?.toString().padStart(2, '0')}/{pm.expYear?.toString().slice(-2)}</span>
                      {pm.isDefault && <span className="default-badge">Default</span>}
                    </div>
                  </label>
                ))}
              </div>

              <button
                className="btn btn-link add-new-card"
                onClick={() => navigate(`/portal/${accessToken}/payment-setup`)}
              >
                + Add a new card
              </button>
            </div>

            <div className="payment-modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowPaymentModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleConfirmPayment}
                disabled={!selectedPaymentMethod}
              >
                Pay {formatCurrency(
                  invoice.paymentOptions?.[selectedPaymentOption]?.amount || invoice.totalAmount,
                  invoice.currency
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortalInvoiceDetail;
